import React, { useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { getPostBySlug, blogPosts } from "../data/blog-posts";
import TCLogo from "../assets/images/TravelCarma-Logo.png";

import ImgTech from "../assets/images/banner-images/banner-enterprise.jpg";
import ImgAI from "../assets/images/blog-ai-banner.svg";
import ImgIndustry from "../assets/images/banner-images/banner-tdx.jpg";
import ImgAPI from "../assets/images/banner-images/banner-development-api.jpg";

const categoryImages = {
  "Travel Technology": ImgTech,
  "AI in Travel": ImgAI,
  "Industry Insights": ImgIndustry,
  "API Integration": ImgAPI,
};

const categoryColors = {
  "Travel Technology": "#0d6efd",
  "AI in Travel": "#e85d04",
  "Industry Insights": "#6f42c1",
  "API Integration": "#198754",
};

const formatDate = (dateStr) => {
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
};

const BlogPost = () => {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!post) {
    return (
      <div className="container py-5 text-center">
        <h2>Article Not Found</h2>
        <p className="text-secondary">The article you are looking for does not exist.</p>
        <Link to="/blog" className="btn btn-primary mt-3">Back to Blog</Link>
      </div>
    );
  }

  const relatedPosts = blogPosts
    .filter(p => p.slug !== slug && p.category === post.category)
    .slice(-3)
    .reverse();

  const canonicalUrl = `https://travelcarma.com/blog/${post.slug}`;
  const heroImage = categoryImages[post.category] || ImgTech;

  const schemaOrg = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.metaDescription,
    "author": {
      "@type": "Person",
      "name": post.author
    },
    "datePublished": post.date,
    "dateModified": post.date,
    "publisher": {
      "@type": "Organization",
      "name": "TravelCarma",
      "url": "https://travelcarma.com",
      "logo": {
        "@type": "ImageObject",
        "url": "https://travelcarma.com/logo.png"
      }
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": canonicalUrl
    },
    "keywords": post.tags.join(", "),
    "articleSection": post.category,
    "timeRequired": `PT${parseInt(post.readTime)}M`
  };

  return (
    <div className="page-inside">
      <Helmet>
        <title>{post.metaTitle}</title>
        <meta name="description" content={post.metaDescription} />
        <meta name="keywords" content={post.tags.join(", ")} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.metaTitle} />
        <meta property="og:description" content={post.metaDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={heroImage} />
        <meta property="article:published_time" content={post.date} />
        <meta property="article:author" content={post.author} />
        <meta property="article:section" content={post.category} />
        {post.tags.map(tag => (
          <meta key={tag} property="article:tag" content={tag} />
        ))}
        <script type="application/ld+json">{JSON.stringify(schemaOrg)}</script>
      </Helmet>

      {/* Hero */}
      <div className="page-title d-flex align-items-center mb-0" style={{ minHeight: 220 }}>
        <div className="container" style={{ position: "relative", zIndex: 2 }}>
          <div className="row justify-content-center">
            <div className="col-lg-9">
              <span
                style={{
                  display: "inline-block",
                  background: categoryColors[post.category] || "#0d6efd",
                  color: "#fff", fontSize: 12, fontWeight: 600,
                  padding: "3px 12px", borderRadius: 20, marginBottom: 12
                }}
              >
                {post.category}
              </span>
              <h1 className="text-white fw-bold" style={{ fontSize: "clamp(22px, 3vw, 34px)", lineHeight: 1.3 }}>
                {post.title}
              </h1>
              <div className="d-flex flex-wrap align-items-center mt-3" style={{ gap: 16, fontSize: 13, color: "rgba(255,255,255,0.85)" }}>
                <span><i className="far fa-user me-1"></i>{post.author}</span>
                <span><i className="far fa-calendar me-1"></i>{formatDate(post.date)}</span>
                <span><i className="far fa-clock me-1"></i>{post.readTime} read</span>
              </div>
            </div>
          </div>
        </div>
        <img className="page-title-img" src={heroImage} alt={post.title} />
      </div>

      {/* Breadcrumb */}
      <div style={{ background: "#f8f9fa", borderBottom: "1px solid #eee" }}>
        <div className="container py-2">
          <nav aria-label="breadcrumb">
            <ol className="breadcrumb mb-0" style={{ fontSize: 13 }}>
              <li className="breadcrumb-item"><Link to="/">Home</Link></li>
              <li className="breadcrumb-item"><Link to="/blog">Blog</Link></li>
              <li className="breadcrumb-item active" aria-current="page">{post.category}</li>
            </ol>
          </nav>
        </div>
      </div>

      {/* Content */}
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-8">
            {/* Excerpt */}
            <p style={{ fontSize: 18, color: "#444", lineHeight: 1.7, borderLeft: "4px solid #0d6efd", paddingLeft: 20, marginBottom: 32, fontStyle: "italic" }}>
              {post.excerpt}
            </p>

            {/* Article Content */}
            <div
              className="blog-content"
              dangerouslySetInnerHTML={{ __html: post.content }}
              style={{
                fontSize: 16,
                lineHeight: 1.85,
                color: "#333"
              }}
            />

            {/* Tags */}
            <div className="mt-5 pt-4" style={{ borderTop: "1px solid #eee" }}>
              <span className="fw-semibold me-2" style={{ fontSize: 14 }}>Tags:</span>
              {post.tags.map(tag => (
                <span key={tag} style={{ display: "inline-block", fontSize: 12, color: "#444", background: "#f0f0f0", borderRadius: 16, padding: "3px 12px", marginRight: 6, marginBottom: 6 }}>
                  #{tag}
                </span>
              ))}
            </div>

            {/* Author card */}
            <div className="mt-4 p-4" style={{ background: "#f8f9fa", borderRadius: 10, border: "1px solid #eee" }}>
              <div className="d-flex align-items-center">
                {post.author === "TravelCarma Team" ? (
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#f0f4ff", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, border: "1px solid #dde" }}>
                    <img src={TCLogo} alt="TravelCarma Team" style={{ width: 38, objectFit: "contain" }} />
                  </div>
                ) : (
                  <div style={{ width: 52, height: 52, borderRadius: "50%", background: "#0d6efd", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontWeight: 700, fontSize: 20, flexShrink: 0 }}>
                    {post.author.charAt(0)}
                  </div>
                )}
                <div className="ms-3">
                  <div className="fw-bold" style={{ fontSize: 15 }}>{post.author}</div>
                  <div className="text-secondary" style={{ fontSize: 13 }}>TravelCarma — Enterprise Travel Technology</div>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-5 p-4 text-center" style={{ background: "linear-gradient(135deg, #0d6efd 0%, #0a58ca 100%)", borderRadius: 12, color: "#fff" }}>
              <h4 className="fw-bold mb-2">Ready to Modernize Your Travel Technology?</h4>
              <p className="mb-3" style={{ opacity: 0.9, fontSize: 15 }}>
                TravelCarma helps travel companies AI-enable workflows, integrate supplier APIs, and build bespoke travel platforms.
              </p>
              <Link to="/contact-us" className="btn btn-light btn-sm px-4" style={{ borderRadius: 30, fontWeight: 600 }}>
                Talk to Our Team
              </Link>
            </div>

            {/* Navigation */}
            <div className="d-flex justify-content-between mt-5 pt-3" style={{ borderTop: "1px solid #eee" }}>
              <Link to="/blog" className="btn btn-outline-secondary btn-sm" style={{ borderRadius: 20 }}>
                ← All Articles
              </Link>
            </div>
          </div>

          {/* Sidebar */}
          <div className="col-lg-4 mt-5 mt-lg-0">
            <div style={{ position: "sticky", top: 80 }}>
              {/* Related Posts */}
              {relatedPosts.length > 0 && (
                <div className="mb-4">
                  <h6 className="fw-bold mb-3 text-uppercase" style={{ fontSize: 12, letterSpacing: 1, color: "#888" }}>
                    Related Articles
                  </h6>
                  {relatedPosts.map(rp => (
                    <div key={rp.id} className="mb-3 pb-3" style={{ borderBottom: "1px solid #f0f0f0" }}>
                      <Link to={`/blog/${rp.slug}`} style={{ textDecoration: "none", color: "#1a1a2e", fontSize: 14, fontWeight: 500, lineHeight: 1.4, display: "block", marginBottom: 4 }}>
                        {rp.title}
                      </Link>
                      <span style={{ fontSize: 12, color: "#999" }}>{formatDate(rp.date)}</span>
                    </div>
                  ))}
                </div>
              )}

              {/* Category Explore */}
              <div className="p-4" style={{ background: "#f8f9fa", borderRadius: 10 }}>
                <h6 className="fw-bold mb-3" style={{ fontSize: 13 }}>Explore by Topic</h6>
                {["Travel Technology", "AI in Travel", "Industry Insights", "API Integration"].map(cat => (
                  <Link
                    key={cat}
                    to="/blog"
                    onClick={() => {}}
                    style={{
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      padding: "8px 0", borderBottom: "1px solid #eee", textDecoration: "none",
                      color: cat === post.category ? "#0d6efd" : "#444", fontSize: 13,
                      fontWeight: cat === post.category ? 600 : 400
                    }}
                  >
                    <span>{cat}</span>
                    <span style={{ color: "#bbb" }}>→</span>
                  </Link>
                ))}
              </div>

              {/* Contact CTA */}
              <div className="mt-4 p-4 text-center" style={{ background: "#fff3cd", borderRadius: 10, border: "1px solid #ffc107" }}>
                <h6 className="fw-bold mb-2" style={{ fontSize: 14 }}>Have a Technology Challenge?</h6>
                <p style={{ fontSize: 13, color: "#555", marginBottom: 12 }}>
                  Our team specializes in enterprise travel technology modernization.
                </p>
                <Link to="/contact-us" className="btn btn-warning btn-sm px-4" style={{ borderRadius: 20, fontSize: 12 }}>
                  Get in Touch
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;
